import { useGateValue } from "@statsig/react-bindings";
import { queryOptions, skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "@/lib/http.ts";
import { ONBOARDING_V3_GATE } from "@/types/onboarding.ts";
import {
  type GetSuggestedTasksResp,
  NEW_CHAT_TASK_SUGGESTIONS_GATE,
  type RecordSuggestedTasksShownReq,
} from "@/types/suggestedTasks.ts";

/**
 * Suggestion cards for the New Chat page.
 *
 * The server draws a fresh sample on every request (random, or under onboarding
 * v3 the set picked for this user), so the caching here is what decides how
 * often the grid changes under the user. Both settings
 * below are load-bearing behavior, not performance tuning, and they pull in
 * opposite directions on purpose:
 *
 * `staleTime: Infinity` holds the grid still while the page is open. Under the
 * defaults, a refetch on window focus would reshuffle it every time the user
 * tabbed away and back — the card they were reaching for replaced by a
 * different one under the cursor.
 *
 * `gcTime: 0` drops the cache the moment the page unmounts, so coming back
 * draws again. Without it the two settings compound: the cached sample would
 * outlive the page by the default five minutes and a user moving between a
 * thread and New Chat would see the same twelve cards every time, making a
 * catalog of hundreds look like a fixed menu of twelve.
 *
 * Together: still while you are looking at it, fresh when you come back.
 */
export const suggestedTasksQuery = (enabled = true) =>
  queryOptions({
    queryKey: ["suggestedTasks"] as const,
    // `skipToken` rather than `enabled: false`, so a gated-off page makes no
    // request at all instead of holding a pending query forever. The server
    // checks the same gates regardless — this only saves the round trip.
    queryFn: enabled
      ? async ({ signal }) => {
          const { data } = await api.get<GetSuggestedTasksResp>("/api/a/suggested-tasks", {
            signal,
          });
          return data;
        }
      : skipToken,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    // A blank-page cure that blanks the page itself on a flaky network is worse
    // than the disease; the composer falls back to its own chips instead of
    // retrying at length.
    retry: 1,
  });

/**
 * Suggestions for the New Chat page, or nothing at all when the gate is closed.
 *
 * With it closed this resolves to no data, and the composer falls through to
 * the chip row it had before the feature existed — so the off state is the old
 * page rather than an empty one.
 *
 * Onboarding v3 brings the suggestions with it, so either gate opens them; the
 * server picks them for the user under that one.
 */
export const useSuggestedTasks = () => {
  // Both read on every render, so each gate's exposure is logged for everyone.
  const suggestionsGate = useGateValue(NEW_CHAT_TASK_SUGGESTIONS_GATE);
  const onboardingV3 = useGateValue(ONBOARDING_V3_GATE);
  return useQuery(suggestedTasksQuery(suggestionsGate || onboardingV3));
};

/**
 * Tell the server which suggestions were put on screen, once per draw, so it
 * does not suggest them again soon and picks the next visit's set without them.
 * Only when the server marked the response `personalized`: outside that, a draw
 * has nothing to record. The flag and not a `picked` task, because an account's
 * first draw in the feature is random, and reporting it is what starts the
 * picking. Taking the server's word rather than reading the picking gate here
 * keeps the two sides from disagreeing, which would leave a set never reported
 * and so never replaced.
 *
 * Fire-and-forget: a lost report costs the user a repeat, never the page.
 */
export const useReportSuggestionsShown = (personalized: boolean, taskIds: readonly string[]) => {
  const { mutate } = useMutation({
    mutationFn: async (body: RecordSuggestedTasksShownReq) => {
      await api.post("/api/a/suggested-tasks/shown", body);
    },
  });
  const key = taskIds.join(",");
  useEffect(() => {
    if (!personalized || key === "") return;
    mutate({ taskIds: key.split(",") });
  }, [personalized, key, mutate]);
};
